import React, { useRef, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, Play, SkipForward, Loader2 } from "lucide-react";
import type { GameScenario } from "../../data/scenarios/types";

interface SelectionVideoModalProps {
  isOpen: boolean;
  choice: GameScenario["choices"][number] | undefined;
  choiceText: string;
  currentScene: number;
  pendingChoiceIndex: number | null;
  modalSelectionIndex: number | null;
  onClose: () => void;
  onVideoEnd: (refKey: string) => void;
  onSkip: () => void;
  onProceedToResult?: () => void;
  safePlayVideo: (video: HTMLVideoElement) => Promise<boolean>;
}

export const SelectionVideoModal = ({
  isOpen,
  choice,
  choiceText,
  currentScene,
  pendingChoiceIndex,
  modalSelectionIndex,
  onClose,
  onVideoEnd,
  onSkip,
  onProceedToResult,
  safePlayVideo,
}: SelectionVideoModalProps) => {
  const modalVideoRef = useRef<HTMLVideoElement | null>(null);
  const [isModalVideoPlaying, setIsModalVideoPlaying] = useState(false);
  const [isModalVideoReady, setIsModalVideoReady] = useState(false);
  const hasMarkedAsWatchedRef = useRef(false);

  // 모달이 열릴 때마다 상태 초기화
  useEffect(() => {
    if (isOpen) {
      setIsModalVideoReady(false);
      setIsModalVideoPlaying(false);
      hasMarkedAsWatchedRef.current = false;
    }
  }, [isOpen]);

  // ESC 키로 모달 닫기
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      window.addEventListener("keydown", handleEscapeKey);
      return () => {
        window.removeEventListener("keydown", handleEscapeKey);
      };
    }
  }, [isOpen, onClose]);

  // 모달 닫기 시 비디오 정리
  const handleClose = () => {
    if (modalVideoRef.current) {
      modalVideoRef.current.pause();
      modalVideoRef.current.currentTime = 0;
    }
    setIsModalVideoPlaying(false);
    setIsModalVideoReady(false);
    onClose();
  };

  // 모달 비디오 재생/일시정지
  const handleModalVideoAreaClick = (
    event: React.MouseEvent<HTMLDivElement>
  ) => {
    if (
      event.target instanceof HTMLElement &&
      (event.target.tagName === "BUTTON" || event.target.closest("button"))
    ) {
      return;
    }

    const video = modalVideoRef.current;
    if (!video) {
      return;
    }

    const isAtEnd =
      video.duration &&
      !isNaN(video.duration) &&
      (video.currentTime >= video.duration - 0.1 || video.ended);

    if (video.paused || video.ended) {
      if (isAtEnd || video.ended) {
        video.currentTime = 0;
        setTimeout(() => {
          safePlayVideo(video).then((success) => {
            if (success) {
              setIsModalVideoPlaying(true);
            }
          });
        }, 50);
      } else {
        safePlayVideo(video).then((success) => {
          if (success) {
            setIsModalVideoPlaying(true);
          }
        });
      }
    } else {
      video.pause();
      setIsModalVideoPlaying(false);
    }
  };

  // 모달 비디오 스킵
  const handleModalVideoSkip = () => {
    if (modalVideoRef.current) {
      const video = modalVideoRef.current;
      if (video.duration && !isNaN(video.duration)) {
        video.currentTime = video.duration;
      }
      video.pause();
      setIsModalVideoPlaying(false);
      onSkip();
    }
  };

  // 비디오 종료 처리
  const handleVideoEnd = () => {
    setIsModalVideoPlaying(false);
    // 이미 시청한 것으로 표시되었으면 중복 호출 방지
    if (!hasMarkedAsWatchedRef.current && modalSelectionIndex !== null) {
      const refKey = `${currentScene}-${modalSelectionIndex}`;
      onVideoEnd(refKey);
      hasMarkedAsWatchedRef.current = true;
    }

    // "이 방법으로 진행" 버튼을 통해 모달이 열린 경우 결과 단계로 이동
    if (
      pendingChoiceIndex !== null &&
      modalSelectionIndex !== null &&
      pendingChoiceIndex === modalSelectionIndex &&
      onProceedToResult
    ) {
      onProceedToResult();
      handleClose();
    }
  };

  if (!isOpen || !choice) {
    return null;
  }

  const modalContent = (
    <div
      className="fixed top-0 left-0 right-0 bottom-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-md"
    >
      <div className="relative w-full max-w-7xl mx-4">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            handleClose();
          }}
          className="absolute -top-16 -right-0 z-10 flex items-center justify-center w-12 h-12 rounded-full bg-black/60 hover:bg-black/80 text-white transition-all duration-300 hover:scale-110 border border-cyan-400/50 hover:border-cyan-400"
        >
          <X className="h-6 w-6" />
        </button>
        <div
          className="relative overflow-hidden rounded-[1.25rem] border-4 border-cyan-400/70 bg-gradient-to-br from-slate-800/90 via-slate-800/80 to-slate-900/90 shadow-[0_35px_120px_rgba(6,182,212,0.4)] ring-2 ring-cyan-500/20 backdrop-blur-sm aspect-video cursor-pointer"
          onClick={handleModalVideoAreaClick}
        >
          {choice.video?.selection ? (
            <>
              <video
                key={choice.video.selection}
                ref={modalVideoRef}
                src={choice.video.selection}
                playsInline
                className="block h-full w-full object-contain"
                onLoadedData={async () => {
                  setIsModalVideoReady(true);
                  if (modalVideoRef.current) {
                    const success = await safePlayVideo(modalVideoRef.current);
                    if (success) {
                      setIsModalVideoPlaying(true);
                    }
                  }
                }}
                onPlay={() => {
                  setIsModalVideoPlaying(true);
                  // 영상 재생 시작 시 시청한 것으로 표시 (한 번만)
                  if (!hasMarkedAsWatchedRef.current && modalSelectionIndex !== null) {
                    const refKey = `${currentScene}-${modalSelectionIndex}`;
                    onVideoEnd(refKey);
                    hasMarkedAsWatchedRef.current = true;
                  }
                }}
                onPause={() => {
                  setTimeout(() => {
                    if (modalVideoRef.current?.paused) {
                      setIsModalVideoPlaying(false);
                    }
                  }, 100);
                }}
                onEnded={handleVideoEnd}
              />
              {!isModalVideoReady && (
                <div className="absolute inset-0 flex items-center justify-center bg-slate-900/80 backdrop-blur-sm">
                  <div className="flex flex-col items-center gap-3">
                    <Loader2 className="h-16 w-16 animate-spin text-cyan-400" />
                    <span className="text-xl font-semibold uppercase tracking-[0.35em] text-gray-300">
                      Loading...
                    </span>
                  </div>
                </div>
              )}
              {isModalVideoReady && !isModalVideoPlaying && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (modalVideoRef.current) {
                      const video = modalVideoRef.current;
                      const isAtEnd =
                        video.duration &&
                        !isNaN(video.duration) &&
                        (video.currentTime >= video.duration - 0.1 ||
                          video.ended);
                      if (isAtEnd || video.ended) {
                        video.currentTime = 0;
                        setTimeout(() => {
                          safePlayVideo(video).then((success) => {
                            if (success) {
                              setIsModalVideoPlaying(true);
                            }
                          });
                        }, 50);
                      } else {
                        safePlayVideo(video).then((success) => {
                          if (success) {
                            setIsModalVideoPlaying(true);
                          }
                        });
                      }
                    }
                  }}
                  className="absolute inset-0 z-10 flex items-center justify-center bg-black/35 opacity-0 transition-opacity duration-200 hover:opacity-100"
                >
                  <span className="flex h-24 w-24 items-center justify-center rounded-full bg-white/90 text-cyan-500 shadow-lg">
                    <Play className="h-16 w-16" />
                  </span>
                </button>
              )}
              {isModalVideoPlaying && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleModalVideoSkip();
                  }}
                  className="group absolute top-6 right-6 z-10 flex justify-center items-center gap-3 rounded-full bg-black/40 hover:bg-gradient-to-r hover:from-cyan-600 hover:via-cyan-500 hover:to-cyan-600 px-7 py-4 text-2xl font-semibold text-white/80 hover:text-white transition-all duration-300 hover:shadow-[0_4px_16px_rgba(6,182,212,0.6)] hover:-translate-y-0.5 backdrop-blur-md border border-cyan-400/30 hover:border-cyan-400/50 overflow-hidden opacity-60 hover:opacity-100"
                >
                  <span>SKIP</span>
                  <SkipForward className="h-8 w-8 transition-transform duration-300 group-hover:translate-x-1" />
                </button>
              )}
            </>
          ) : (
            <div className="flex aspect-video w-full items-center justify-center bg-slate-900/80 text-lg font-semibold text-gray-300">
              영상이 준비되지 않았습니다.
            </div>
          )}
        </div>
        <div className="mt-7 text-center">
          <h3 className="text-4xl font-bold text-white">{choiceText}</h3>
        </div>
      </div>
    </div>
  );

  return typeof window !== "undefined"
    ? createPortal(modalContent, document.body)
    : null;
};

